import React, { useState, useRef } from "react";
import { COLORS } from "../../../theme/colors";
import Icon from "../../../components/Icon";

/**
 * Build an isolated drag-ghost node for a palette item.
 *
 * Uses createElement + textContent (never innerHTML) so a malicious
 * item.label / item.icon is rendered as inert TEXT, not parsed as HTML.
 * Exported for unit testing (SEC-010).
 */
export function buildDragGhost(item) {
  const ghost = document.createElement("div");
  ghost.style.cssText = `
    position: fixed; top: -9999px; left: -9999px;
    display: flex; align-items: center; gap: 10px;
    padding: 8px 12px; border-radius: 10px;
    background: ${COLORS.surface}; border: 1px solid ${item.color}40;
    box-shadow: 0 4px 16px rgba(0,0,0,0.4);
    font-family: inherit; pointer-events: none; white-space: nowrap;
  `;

  const iconSpan = document.createElement("span");
  iconSpan.style.fontSize = "16px";
  iconSpan.textContent = item.icon == null ? "" : String(item.icon);

  const labelSpan = document.createElement("span");
  labelSpan.style.fontSize = "12px";
  labelSpan.style.fontWeight = "600";
  labelSpan.style.color = COLORS.text;
  labelSpan.textContent = item.label == null ? "" : String(item.label);

  ghost.appendChild(iconSpan);
  ghost.appendChild(labelSpan);
  return ghost;
}

const PaletteItem = ({ item, draggedItem, setDraggedItem, onDropToCanvas }) => {
  const [hovered, setHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0 });
  const ref = useRef(null);

  const handleMouseEnter = (e) => {
    setHovered(true);
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setTooltipPos({ top: rect.top });
    }
  };

  return (
    // `data-palette-type` carries the SAME value the drag puts on the
    // dataTransfer (`paletteType`), so an automated drag can target a
    // palette item by what it IS rather than by the label text inside its
    // <span>. The previous selector had to be
    //   div[draggable="true"]:has(span:text-is("Condition"))
    // which couples a test to the label copy and to the DOM shape of the
    // row — both of which are presentation and both of which are expected
    // to change. Sibling `DraggableNode` already exposes `data-node-type`
    // for exactly this reason; this closes the matching gap on the source
    // side of the same drag.
    <div ref={ref}
      data-testid="palette-item"
      data-palette-type={item.type}
      draggable
      onDragStart={(e) => {
        setDraggedItem(item.type);
        e.dataTransfer.setData("paletteType", item.type);

        // Build an isolated ghost so the browser doesn't screenshot neighboring items.
        // Built via createElement + textContent (no innerHTML) — SEC-010.
        const ghost = buildDragGhost(item);
        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight / 2);
        setTimeout(() => document.body.removeChild(ghost), 0);
      }}
      onDragEnd={() => setDraggedItem(null)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 12px", borderRadius: 10, cursor: "grab",
        background: hovered || draggedItem === item.type ? COLORS.surfaceHover : "transparent",
        border: `1px solid ${hovered ? item.color + "30" : draggedItem === item.type ? COLORS.border : "transparent"}`,
        transition: "all 0.2s", position: "relative",
      }}
    >
      <div style={{
        width: 30, height: 30, borderRadius: 8,
        background: `${item.color}15`, border: `1px solid ${item.color}30`,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
        flexShrink: 0,
      }}>
        {item.iconName
          ? <Icon name={item.iconName} size={16} color={item.color} />
          : item.icon}
      </div>
      <span style={{ fontSize: 12, fontWeight: 500, color: COLORS.text }}>{item.label}</span>

      {/* Tooltip */}
      {hovered && (
        <div style={{
          position: "fixed", left: 240, top: tooltipPos.top - 10, zIndex: 1000,
          width: 260, padding: "14px 16px", borderRadius: 12,
          background: COLORS.surface, border: `1px solid ${item.color}30`,
          boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 20px ${item.color}15`,
          pointerEvents: "none",
          animation: "tooltipIn 0.18s ease-out",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 6,
              background: `${item.color}20`, border: `1px solid ${item.color}40`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12,
            }}>
              {item.iconName
                ? <Icon name={item.iconName} size={14} color={item.color} />
                : item.icon}
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{item.label}</span>
          </div>
          <div style={{
            fontSize: 11.5, color: COLORS.textMuted, lineHeight: 1.55, letterSpacing: "0.01em",
          }}>{item.desc}</div>
          <div style={{
            marginTop: 10, paddingTop: 8, borderTop: `1px solid ${COLORS.border}`,
            fontSize: 10, color: COLORS.textDim, display: "flex", alignItems: "center", gap: 4,
          }}>
            <span style={{ opacity: 0.7 }}>{"\u21E5"}</span> Drag onto canvas to use
          </div>
          {/* Arrow */}
          <div style={{
            position: "absolute", left: -6, top: 20, width: 12, height: 12,
            background: COLORS.surface, border: `1px solid ${item.color}30`,
            borderRight: "none", borderTop: "none",
            transform: "rotate(45deg)",
          }} />
        </div>
      )}
    </div>
  );
};

export default PaletteItem;
